import axios from 'axios';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// TypeScript Interfaces
export interface Rider {
  id: number;
  name: string;
  loginID: string;
}

export interface DeliverySheet {
  id: number;
  unique_id: string;
  rider_id: number;
  rider: Rider;
  branch_id: number;
  branch?: any;
  user_id: number;
  user?: any;
  status_id: number;
  status: {
    id: number;
    name: string;
    badge: string;
  };
  total_consignments: number;
  total_cod_amount: number;
  cod_amount_recieved: number;
  cod_amount_collected: number;
  cod_amount_collected_at: string | null;
  collected_by_id: number | null;
  collectedBy?: any;
  created_at: string;
  updated_at: string;
  dispatched_at?: string;
  checked_in_at?: string;
  closed_at?: string;
  consignments?: any[];
  delivered_count?: number;
  rejected_count?: number;
  reattempt_count?: number;
  pending_count?: number;
}

export interface DeliverySheetFilters {
  check_in?: string | boolean;
  status_id?: number | string;
  rider_id?: number | string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export interface CreateDeliverySheetRequest {
  rider_id: number;
  consignmentIds: number[];
}

export interface UpdateDeliverySheetRequest {
  rider_id: number;
  consignmentIds: number[];
}

// API Functions

/**
 * Get all delivery sheets with filters
 */
export async function getDeliverySheets(filters?: DeliverySheetFilters) {
  try {
    const response = await apiClient.get('/delivery-sheets', { params: filters });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch delivery sheets');
  }
}

/**
 * Get single delivery sheet
 */
export async function getDeliverySheet(id: number) {
  try {
    const response = await apiClient.get(`/delivery-sheets/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch delivery sheet');
  }
}

/**
 * Create delivery sheet
 */
export async function createDeliverySheet(data: CreateDeliverySheetRequest) {
  try {
    const payload = {
      rider_id: data.rider_id,
      consignment_ids: data.consignmentIds,
    };
    const response = await apiClient.post('/delivery-sheets', payload);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to create delivery sheet');
  }
}

/**
 * Update delivery sheet
 */
export async function updateDeliverySheet(id: number, data: UpdateDeliverySheetRequest) {
  try {
    const response = await apiClient.put(`/delivery-sheets/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update delivery sheet');
  }
}

/**
 * Dispatch delivery sheet
 */
export async function dispatchDeliverySheet(id: number) {
  try {
    const response = await apiClient.post(`/delivery-sheets/${id}/dispatch`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to dispatch delivery sheet');
  }
}

/**
 * Check-in delivery sheet
 */
export async function checkInDeliverySheet(id: number) {
  try {
    const response = await apiClient.post(`/delivery-sheets/${id}/check-in`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to check-in delivery sheet');
  }
}

/**
 * Close delivery sheet
 */
export async function closeDeliverySheet(id: number) {
  try {
    const response = await apiClient.post(`/delivery-sheets/${id}/close`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to close delivery sheet');
  }
}

/**
 * Mark remaining consignments as delivered
 */
export async function markDelivered(id: number) {
  try {
    const response = await apiClient.post(`/delivery-sheets/${id}/mark-all-delivered`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to mark consignments as delivered');
  }
}

/**
 * Remove consignment from delivery sheet
 */
export async function removeConsignment(id: number, consignmentId: number) {
  try {
    const response = await apiClient.delete(`/delivery-sheets/${id}/consignments/${consignmentId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to remove consignment');
  }
}

/**
 * Get failed consignments
 */
export async function getFailedConsignments(page?: number, per_page?: number) {
  try {
    const response = await apiClient.get('/delivery-sheets/failed-consignments', {
      params: { page, per_page }
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch failed consignments');
  }
}

/**
 * Get riders for dropdown
 */
export async function getRiders() {
  try {
    const response = await apiClient.get('/delivery-sheets/riders');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch riders');
  }
}

/**
 * Update consignment status within a delivery sheet (Rejected / Reattempt)
 */
export async function updateConsignmentStatus(
  deliverySheetId: number,
  data: { consignment_no: string; action: 'Rejected' | 'Reattempted'; reason: string }
) {
  try {
    const response = await apiClient.post(
      `/delivery-sheets/${deliverySheetId}/update-consignment-status`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update consignment status');
  }
}

/**
 * Print delivery sheet (opens PDF in new tab)
 */
export function printDeliverySheet(id: number) {
  const publicUrl = `${API_BASE_URL}/public/delivery-sheets/${id}/print`;
  window.open(publicUrl, '_blank', 'noopener,noreferrer');
}
